import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import useBreakpoint from 'hooks/use-breakpoint';
import NavLayout from 'app/layouts/nav-layout';
import { RegisterContext } from './register.context';
import { Styled } from './register-intro.styled';
import { RegisterProviderProps } from './register.types';
import Routing from './routing';
import { getRegisterStepIndex } from './register.utils';

const MobileSupportComponent = lazy(
    () => import('features/register/components/mobile-support'),
);
const Sidebar = lazy(
    () => import('features/register/components/sidebar/sidebar'),
);
const MobileStepper = lazy(
    () => import('features/register/components/mobile-stepper'),
);

const RegisterProvider: React.FC<RegisterProviderProps> = (
    props,
): JSX.Element => {
    const { biggerThan, lowerThan } = useBreakpoint();
    const [current_step, setStep] = useState<string>(props?.current_step || '');
    const [active_step, setActiveStep] = useState<number>(0);
    const [bankDraft, setBankDraft] = useState<{
        iban: string;
        backup_iban: string;
    } | null>(null);
    const isTicket = current_step === 'ticket';
    const shouldHideContentScrollbar = ['profile', 'documents'].includes(current_step);

    useEffect(() => {
        const nextActiveStep = getRegisterStepIndex(props.steps, current_step);

        if (nextActiveStep >= 0) {
            setActiveStep((previous) =>
                previous === nextActiveStep ? previous : nextActiveStep,
            );
        }
    }, [current_step, props.steps]);

    return (
        <RegisterContext.Provider
            value={{
                ...props,
                current_step,
                setStep,
                active_step,
                setActiveStep,
                bankDraft,
                setBankDraft,
            }}>
            <Styled.Container>
                {biggerThan('sm') && (
                    <Styled.SidebarContainer>
                        <Sidebar />
                    </Styled.SidebarContainer>
                )}

                <Styled.Card>
                    <NavLayout />

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            overflowY: { xs: 'visible', sm: isTicket ? 'hidden' : 'auto' },
                            overflowX: 'hidden',
                            scrollBehavior: 'smooth',
                            px: { xs: 1.25, sm: 2, md: 0.5 },
                            pb: isTicket ? 0 : { xs: 2.5, md: 1.5 },
                            scrollbarGutter: shouldHideContentScrollbar ? 'auto' : 'stable',
                            scrollbarWidth: shouldHideContentScrollbar ? 'none' : 'thin',
                            msOverflowStyle: shouldHideContentScrollbar ? 'none' : 'auto',
                            '&::-webkit-scrollbar': {
                                display: shouldHideContentScrollbar ? 'none' : 'block',
                                width: shouldHideContentScrollbar ? 0 : 8,
                            },
                        }}>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 1720,
                                mx: 'auto',
                                height: isTicket ? '100%' : 'auto',
                                minHeight: 0,
                            }}>
                            {lowerThan('sm') && current_step !== 'ticket' && (
                                <Suspense>
                                    <MobileStepper />
                                </Suspense>
                            )}

                            <Routing />
                        </Box>
                    </Box>
                </Styled.Card>
            </Styled.Container>

            {lowerThan('sm') && current_step !== 'ticket' && (
                <Suspense>
                    <MobileSupportComponent />
                </Suspense>
            )}
        </RegisterContext.Provider>
    );
};

export default RegisterProvider;
